/**
 * The juice library (ART-BIBLE §7).
 *
 * Every SimEvent in the contract becomes motion here. Two rules hold the whole file
 * together: nothing starts instantly (ANTIC), nothing stops dead (SETTLE), and only one
 * spectacle takes the camera at a time (Anim.playBig).
 *
 * "Cut motion with stillness: a BEAT before a payout is worth more than another particle."
 */

import {
  Group, Mesh, PlaneGeometry, MeshBasicMaterial, Vector3, Color, AdditiveBlending,
  DoubleSide, CanvasTexture, SRGBColorSpace, LinearFilter,
} from 'three';
import type { BankBreakMode, MachineId, SimEvent, Tag } from '../../../shared/contract';
import {
  ANTIC, SNAP, SETTLE, BEAT, RATCHET, FLICKER, SLOWMO, SLOWMO_SCALE, Timeline, ease, lerp,
} from './tween';
import {
  NEON, MATERIALS, PAPIER, INK, BLUTROT, SENF, CREME, NACHT, GUMMI, BAKELIT, TAG_COLOR,
  MACHINE_LOOK, css, mixHex,
} from '../art/palette';
import { FONT_SIGN, FONT_MONO } from '../art/textures';
import { toon } from '../art/toon';
import type { Stage } from '../scene/stage';
import type { CameraRig } from '../scene/camera';
import type { Anim } from './tween';
import type { Room, Croupier } from '../prefabs/room';
import type { Cabinet } from '../prefabs/cabinets';
import { loudestTag, type TokenSystem } from '../tokens/tokens';
import type { SpriteField } from '../fx/sprites';
import type { CoinShower } from '../fx/coins';

export interface FxContext {
  stage: Stage;
  anim: Anim;
  rig: CameraRig;
  room: Room;
  croupier: Croupier;
  tokens: TokenSystem;
  sparks: SpriteField;
  smoke: SpriteField;
  coins: CoinShower;
  cabinets: Map<MachineId, Cabinet>;
  fxRoot: Group;
  /** the cabinet the camera is currently on */
  active: MachineId | null;
  /**
   * **contract v1** — every cabinet's origin in the sim's one shared Rapier world, as last
   * seen on a `CabinetState` or an `AisleCabinet` row. Token positions and every event `at`
   * are WORLD-space; the renderer draws them in the cabinet's frame, so it must subtract
   * this. Kept here (and not read from the snapshot on demand) so an effect fired between
   * two snapshots still lands in the right place. See `fxLocal` / `fxWorld`.
   */
  origins: Map<MachineId, Vector3>;
  /** the origin of the cabinet the token pool is parented to; the fallback for `fxLocal` */
  origin: Vector3;
  /** live TILT lockouts: machine → sim tick of release (v1 `tiltLockout.until`) */
  lockouts: Map<MachineId, number>;
  /** run-state mirror, so effects can print honest numbers */
  hud: {
    muenzen: number; tickets: number; glueck: number; verdacht: number;
    straehne: number; floor: number; topf01: number; topfMax: number; tilt01: number;
    /** the sim tick of the last snapshot — ticks are the only clock the contract has */
    tick: number;
  };
  /** caption line under the showroom, used by the fixture player */
  say?(text: string, sub?: string): void;
  /** 0..1 edge darkening — VERDACHT, Abrechnung, floor transitions */
  setVignette?(v: number): void;
}

const _v = new Vector3();

function cab(ctx: FxContext, id?: MachineId | null): Cabinet | null {
  const key = id ?? ctx.active;
  return key ? ctx.cabinets.get(key) ?? null : null;
}

function worldAnchor(c: Cabinet, key: keyof Cabinet['anchors'], out = new Vector3()): Vector3 {
  out.copy(c.anchors[key]);
  c.group.updateMatrixWorld();
  return c.group.localToWorld(out);
}

// ================================================================ coordinates (v1)

/** the sim-world origin of a cabinet, falling back to the focused one */
export function simOrigin(ctx: FxContext, machine?: MachineId | null): Vector3 {
  const id = machine ?? ctx.active;
  return (id && ctx.origins.get(id)) || ctx.origin;
}

/**
 * Sim WORLD position → the cabinet's frame, i.e. `P − O`.
 *
 * This one subtraction is the whole of contract v1's coordinate decision on the render
 * side. The sim runs one contiguous world with cabinets 16 units apart (so that a token
 * can physically roll from one cabinet into its neighbour — *Der Deckel* — instead of
 * being scripted across a seam). The renderer parents the token pool to the cabinet's
 * `Object3D`, which is what lets a travelling cabinet keep its pile shape: when Der
 * Croupier wheels the machine downstairs only `origin` changes, and one vector subtraction
 * per cabinet per frame replaces a matrix multiply per token.
 */
export function fxLocal(ctx: FxContext, at: [number, number, number], machine?: MachineId | null, out = new Vector3()): Vector3 {
  const o = simOrigin(ctx, machine);
  return out.set(at[0] - o.x, at[1] - o.y, at[2] - o.z);
}

/** sim WORLD position → render world, through the cabinet the pool hangs under */
export function fxWorld(ctx: FxContext, at: [number, number, number], machine?: MachineId | null): Vector3 {
  const out = fxLocal(ctx, at, machine);
  const c = cab(ctx, machine);
  if (!c) return out;
  c.group.updateMatrixWorld();
  return c.group.localToWorld(out);
}

// ================================================================ paper

function paperTexture(lines: string[], w = 320, lineH = 26): CanvasTexture {
  const h = Math.max(64, lines.length * lineH + 48);
  const c = document.createElement('canvas');
  c.width = w; c.height = h;
  const g = c.getContext('2d')!;
  g.fillStyle = css(PAPIER);
  g.fillRect(0, 0, w, h);
  // the torn top edge
  g.fillStyle = css(NACHT.void, 0.0);
  g.font = `18px ${FONT_MONO}`;
  g.fillStyle = css(INK, 0.88);
  g.textBaseline = 'middle';
  lines.forEach((l, i) => {
    const y = 26 + i * lineH;
    if (l.startsWith('=')) {
      g.strokeStyle = css(INK, 0.45);
      g.lineWidth = 1.5;
      g.beginPath(); g.moveTo(16, y); g.lineTo(w - 16, y); g.stroke();
      return;
    }
    const bold = l.startsWith('*');
    const txt = bold ? l.slice(1) : l;
    g.font = bold ? `600 21px ${FONT_SIGN}` : `18px ${FONT_MONO}`;
    const parts = txt.split('|');
    if (parts.length === 2) {
      g.textAlign = 'left'; g.fillText(parts[0], 16, y);
      g.textAlign = 'right'; g.fillText(parts[1], w - 16, y);
    } else {
      g.textAlign = bold ? 'center' : 'left';
      g.fillText(txt, bold ? w / 2 : 16, y);
    }
  });
  const t = new CanvasTexture(c);
  t.colorSpace = SRGBColorSpace;
  t.minFilter = LinearFilter; t.magFilter = LinearFilter;
  t.generateMipmaps = false;
  return t;
}

// ================================================================ effects

/**
 * COIN FOUNTAIN — the payout gesture. ANTIC (the machine gathers itself), then the arc,
 * then coins rattle into the tray. Never instant, never silent-looking.
 */
export function fxPayout(ctx: FxContext, muenzen: number, tickets: number, machine: MachineId): Timeline {
  const c = cab(ctx, machine);
  const tl = new Timeline();
  if (!c) return tl;
  const from = worldAnchor(c, 'payout');
  const tray = worldAnchor(c, 'tray');
  const n = Math.max(4, Math.min(120, Math.round(muenzen / 6) + 6));
  const dir = _v.copy(tray).sub(from).normalize().clone();
  dir.y = Math.max(0.55, dir.y + 0.7);

  tl.then(ANTIC, ease.inQuad, (u) => { c.group.position.y = -0.22 * u; })
    .call(() => {
      ctx.coins.fountain(from, dir, Math.round(n * 0.6), { power: 21, spread: 0.55, floorY: tray.y - 0.4 });
      for (let i = 0; i < 12; i++) {
        ctx.sparks.spawn({
          pos: [from.x, from.y, from.z],
          vel: [(Math.random() - 0.5) * 9, 5 + Math.random() * 7, (Math.random() - 0.5) * 5],
          gravity: 22, life: 0.5 + Math.random() * 0.4, size: 0.7, sizeEnd: 0.05,
          color: NEON.amber.core, colorEnd: NEON.amber.halo,
        });
      }
    })
    .then(SETTLE, ease.outBack, (u) => { c.group.position.y = -0.22 * (1 - u); })
    .call(() => {
      ctx.coins.fountain(from, dir, Math.round(n * 0.4), { power: 16, spread: 0.8, floorY: tray.y - 0.4 });
    });

  if (tickets > 0) tl.call(() => { ctx.anim.play(fxTickets(ctx, tickets, machine)); }, ANTIC + SNAP);
  return tl;
}

/**
 * TICKET RATCHET — a paper strip clacking out of the slot one step at a time, then torn.
 * Faked physics: the strip is a plane that grows in RATCHET steps and swings on a hinge.
 */
export function fxTickets(ctx: FxContext, count: number, machine: MachineId): Timeline {
  const c = cab(ctx, machine);
  const tl = new Timeline();
  if (!c) return tl;
  const at = worldAnchor(c, 'ticket');
  const steps = Math.max(2, Math.min(14, Math.round(count / 3) + 2));

  const strip = new Mesh(new PlaneGeometry(1, 1), toon({
    color: 0xffffff, ramp: 'SOFT2', rim: 0.15, halftone: 0, side: DoubleSide,
    map: paperTexture(['TICKETS', '=', ...Array.from({ length: 3 }, () => `${count} STÜCK`)], 128, 22),
    transparent: true,
  }));
  strip.userData.noInk = true;
  const pivot = new Group();
  pivot.position.copy(at);
  pivot.add(strip);
  ctx.fxRoot.add(pivot);

  const W = 2.0;
  for (let i = 0; i < steps; i++) {
    tl.then(RATCHET, ease.detent, (u) => {
      const len = W * ((i + u) * 0.55 + 0.3);
      strip.scale.set(W, len, 1);
      strip.position.set(0, -len / 2, 0.2);
      // the strip swings a little as it lengthens: paper has weight
      pivot.rotation.z = Math.sin((i + u) * 1.7) * 0.05 * Math.min(1, len / 4);
    });
  }
  tl.wait(BEAT * 0.5)
    .then(SNAP, ease.outQuad, (u) => { pivot.rotation.z += 0.12 * u; })
    .call(() => {
      const len = strip.scale.y;
      for (let i = 0; i < 6; i++) {
        ctx.sparks.spawn({
          pos: [at.x, at.y - len * 0.5, at.z], vel: [(Math.random() - 0.5) * 4, -2 - Math.random() * 3, 1],
          gravity: 14, life: 0.7, size: 0.5, sizeEnd: 0.1, color: PAPIER, colorEnd: CREME.shade,
        });
      }
    })
    .then(SETTLE, ease.outCubic, (u) => {
      pivot.position.y = at.y - u * 6;
      (strip.material as { opacity: number }).opacity = 1 - u;
    })
    .call(() => { pivot.removeFromParent(); });
  return tl;
}

/** DER TOPF slurp — a near-miss is banked, visibly, into the pot */
export function fxTopfFeed(ctx: FxContext, machine: MachineId, amount: number): Timeline {
  const c = cab(ctx, machine);
  const tl = new Timeline();
  if (!c) return tl;
  const from = worldAnchor(c, 'face');
  const to = new Vector3();
  c.group.updateMatrixWorld();
  c.topf.group.getWorldPosition(to);

  const nDots = Math.max(3, Math.min(14, Math.round(amount / 4)));
  tl.then(SNAP + SETTLE, ease.inOutCubic, (u) => {
    for (let i = 0; i < nDots; i++) {
      const k = (u + i / nDots) % 1;
      const arc = Math.sin(k * Math.PI) * 4.5;
      ctx.sparks.push(
        lerp(from.x, to.x, k), lerp(from.y, to.y, k) + arc, lerp(from.z, to.z, k),
        0.9 * (1 - k * 0.4), mixHex(NEON.amber.core, NEON.amber.halo, k),
      );
    }
  });
  return tl;
}

/** the pot's level, moved with a settle rather than snapped */
export function fxTopfLevel(c: Cabinet, from: number, to: number): Timeline {
  return new Timeline().then(SETTLE, ease.outBack, (u) => c.topf.set(lerp(from, to, u)));
}

/**
 * STRÄHNE — the streak meter as a flame (contract v1: `{ from, to }`).
 *
 * The event exists so the two directions can be OPPOSITE motions, which is the whole
 * reason it is an event and not a number on the snapshot:
 *   · ignite (to > from) — ANTIC duck, then the flame LEAPS and settles. It is your luck,
 *     so it eases (ART-BIBLE §7.2).
 *   · snuff  (to <= from) — one SNAP and it is gone, leaving smoke. A dud does not fade
 *     the streak out politely. Losing should feel like losing.
 */
export function fxStraehne(ctx: FxContext, from: number, to: number, machine: MachineId): Timeline {
  const c = cab(ctx, machine);
  const tl = new Timeline();
  if (!c) return tl;
  const at = worldAnchor(c, 'face');
  at.y += 5.5;

  if (to <= from) {
    // the snuff: the flame collapses into itself, then tar-coloured smoke drifts up
    tl.then(SNAP, ease.outQuad, (u) => {
      const h = (1 - u) * 3.4 * from;
      for (let i = 0; i < 6; i++) {
        const k = i / 6;
        ctx.sparks.push(at.x, at.y + k * h, at.z, 1.2 * (1 - u) * (1 - k * 0.5),
          mixHex(NEON.amber.core, NACHT.base, u));
      }
    }).call(() => {
      for (let i = 0; i < 5; i++) {
        ctx.smoke.spawn({
          pos: [at.x + (Math.random() - 0.5) * 1.6, at.y + 0.4, at.z],
          vel: [0, 1.4 + Math.random(), 0], drag: 0.9, life: 1.1 + Math.random() * 0.5,
          size: 0.5, sizeEnd: 2.0, color: mixHex(NACHT.lift, CREME.shade, 0.4),
          colorEnd: NACHT.deep, fade: 'inOut',
        });
      }
      c.marquee.setIntensity(0.55);
    }).then(SETTLE, ease.outCubic, (u) => c.marquee.setIntensity(0.55 + 0.45 * u));
    return tl;
  }

  const n = Math.round(to * 8);
  tl.call(() => { if (to >= 3) ctx.say?.(`STRÄHNE ×${to}`, 'Der Automat läuft heiß'); })
    .then(ANTIC, ease.inQuad, (u) => c.marquee.setIntensity(1 - 0.35 * u))
    .then(SETTLE, ease.outBack, (u) => {
      // the leap: the flame overshoots its new height and settles back onto it
      c.marquee.setIntensity(0.65 + 0.55 * u);
      for (let i = 0; i < n; i++) {
        const k = i / Math.max(1, n);
        ctx.sparks.push(at.x + Math.sin(u * 18 + i) * 0.5, at.y + k * 3.4 * lerp(from, to, u), at.z,
          1.6 * (1 - k * 0.6), mixHex(NEON.amber.core, BLUTROT.lit, k));
      }
    })
    .then(BEAT * 1.6, ease.linear, (u) => {
      for (let i = 0; i < n; i++) {
        const k = i / Math.max(1, n);
        const wob = Math.sin(u * 14 + i * 1.7) * 0.5;
        ctx.sparks.push(
          at.x + wob, at.y + k * 3.4 * to, at.z,
          1.5 * (1 - k * 0.6) * (0.7 + 0.3 * Math.sin(u * 20 + i)),
          mixHex(NEON.amber.core, BLUTROT.lit, k),
        );
      }
      c.marquee.setIntensity(1.2 - 0.2 * u);
    });
  return tl;
}

/**
 * PAYOUT LADDER (v1: `payoutStage` now names its `machine`) — the House showing its
 * working, one rung at a time, on the cabinet that is actually paying rather than on
 * whichever one the camera happens to be pointed at. House arithmetic: RATCHET detents at
 * constant velocity, never eased.
 */
const STAGE_NAME: Record<string, string> = {
  base: 'GRUNDWERT', material: 'MATERIAL', straehne: 'STRÄHNE', topf: 'DER TOPF', floor: 'ETAGE',
};

export function fxPayoutStage(
  ctx: FxContext, stage: string, before: number, after: number, machine: MachineId,
): Timeline {
  const c = cab(ctx, machine);
  const tl = new Timeline();
  if (!c) return tl;
  const at = worldAnchor(c, 'payout');
  const gain = Math.max(0, after - before);
  const steps = Math.max(1, Math.min(6, Math.round(Math.log2(1 + gain / 20) + 1)));

  tl.call(() => ctx.say?.(STAGE_NAME[stage] ?? stage.toUpperCase(),
    `${before.toLocaleString('de-DE')} → ${after.toLocaleString('de-DE')}`));
  for (let i = 0; i < steps; i++) {
    tl.then(RATCHET, ease.detent, (u) => {
      const k = (i + u) / steps;
      c.marquee.setIntensity(1 + k * 0.9);
      ctx.sparks.push(at.x + (k - 0.5) * 3.2, at.y + 0.4 + k * 2.6, at.z, 0.75,
        mixHex(NEON.amber.core, NEON.amber.halo, k));
    });
  }
  tl.then(SETTLE, ease.outCubic, (u) => c.marquee.setIntensity(lerp(1.9, 1, u)));
  return tl;
}

/** GLÜCK spent — the world holds still for a moment and one thing sparkles */
export function fxGlueck(ctx: FxContext, stream: string): Timeline {
  const tl = new Timeline();
  const c = cab(ctx);
  const at = c ? worldAnchor(c, 'face') : new Vector3(0, 14, 0);
  tl.call(() => { ctx.stage.timescale = 0.35; ctx.say?.('GLÜCK', stream); })
    .then(BEAT * 0.9, ease.pulse, (u) => {
      for (let i = 0; i < 10; i++) {
        const a = (i / 10) * Math.PI * 2 + u * 1.2;
        const r = 3.2 + u * 3.5;
        ctx.sparks.push(at.x + Math.cos(a) * r, at.y + Math.sin(a) * r * 0.6, at.z + 1.5,
          0.8 + u * 0.7, mixHex(NEON.gruen.core, NEON.gruen.halo, u));
      }
    })
    .call(() => { ctx.stage.timescale = 1; });
  return tl;
}

/** VERDACHT — the room dims at the edges and the camera peeks at Der Croupier */
export function fxVerdacht(ctx: FxContext, delta: number, reason: string): Timeline {
  const tl = new Timeline();
  const strong = delta >= 8;
  tl.call(() => { ctx.say?.(`VERDACHT +${delta}`, reason); ctx.setVignette?.(0); });
  tl.then(SNAP * 2, ease.outQuad, (u) => ctx.setVignette?.(u * (strong ? 0.85 : 0.45)));
  if (strong) {
    tl.call(() => { ctx.rig.peek(1); ctx.croupier.setPose(1); })
      .wait(BEAT * 1.6)
      .call(() => { ctx.rig.unpeek(); ctx.croupier.setPose(0); });
  }
  tl.wait(BEAT * 0.6)
    .then(SETTLE * 1.6, ease.inOutCubic, (u) => ctx.setVignette?.((strong ? 0.85 : 0.45) * (1 - u)));
  return tl;
}

/** TILT — the cabinet takes the shove and the bubble runs to the edge */
export function fxTilt(ctx: FxContext, machine: MachineId, amount: number): Timeline {
  const c = cab(ctx, machine);
  const tl = new Timeline();
  if (!c) return tl;
  const x0 = c.group.position.x;
  tl.then(SNAP, ease.outQuad, (u) => { c.group.position.x = x0 + Math.sin(u * Math.PI * 3) * 0.5 * amount; })
    .then(SETTLE, ease.outBack, (u) => { c.group.position.x = x0 + Math.sin((1 - u) * Math.PI * 5) * 0.22 * amount; })
    .call(() => { c.group.position.x = x0; });
  return tl;
}

/**
 * TILT lockout — the raspberry buzzer, drawn: everything goes red and stops.
 *
 * v1 gives the lockout an END (`until`, a sim tick), so the bubble no longer sits pinned in
 * the red with no information in it: it walks back toward the centre for exactly as long as
 * the lockout lasts, and the machine wakes with a SETTLE. The walk is LINEAR — a lockout
 * timer is House machinery, and the House does not emote (ART-BIBLE §7.2).
 */
export function fxTiltLockout(ctx: FxContext, machine: MachineId, secs: number): Timeline {
  const c = cab(ctx, machine);
  const tl = new Timeline();
  if (!c) return tl;
  const buzz = FLICKER * 1.3;                       // one buzzer pulse, both halves
  const hold = Math.max(SNAP, secs - 5 * buzz * 2 - SETTLE);

  tl.call(() => {
    ctx.say?.('TILT', `Automat gesperrt · ${Math.max(1, Math.round(secs))} s`);
    ctx.rig.kick(0.8);
    c.tilt.set(1);
  });
  for (let i = 0; i < 5; i++) {
    tl.then(buzz, ease.linear, () => c.marquee.setIntensity(0.05))
      .then(buzz, ease.linear, () => c.marquee.setIntensity(1));
  }
  tl.then(hold, ease.linear, (u) => {
    c.marquee.setIntensity(0.35 + 0.2 * u);
    c.tilt.set(1 - u);                              // the bubble runs back toward the centre
  })
    .call(() => { ctx.lockouts.delete(machine); ctx.say?.('FREI', 'Der Automat nimmt wieder an'); })
    .then(SETTLE, ease.outBack, (u) => { c.marquee.setIntensity(lerp(0.55, 1, u)); c.tilt.set(ctx.hud.tilt01 * u); });
  tl.onDone = () => ctx.lockouts.delete(machine);
  return tl;
}

/** JACKPOT — the pot empties into the room */
export function fxJackpot(ctx: FxContext, machine: MachineId, value: number): Timeline {
  const c = cab(ctx, machine);
  const tl = new Timeline();
  if (!c) return tl;
  const from = worldAnchor(c, 'payout');
  tl.call(() => { ctx.say?.('JACKPOT', `${value.toLocaleString('de-DE')} Münzen`); c.topf.set(0.02); })
    .then(BEAT, ease.pulse, (u) => c.marquee.setIntensity(0.2 + u * 1.6))
    .call(() => {
      ctx.coins.fountain(from, new Vector3(0, 1, 0.35), 70, { power: 30, spread: 1.0, floorY: 0.35, life: 3.2 });
    })
    .then(SETTLE * 2, ease.outCubic, (u) => c.marquee.setIntensity(lerp(1.8, 1, u)));
  return tl;
}

/**
 * The five ways a bank goes (contract v1 `BankBreakMode`). Beat 2 of the money shot, and
 * ONLY beat 2 — the strain, the slow-mo, the spectacle camera and the deadpan hold are the
 * same ceremony every time, because that is what makes it read as one game's signature
 * moment rather than five unrelated explosions.
 */
type BreakBeat = (ctx: FxContext, c: Cabinet, burst: Vector3) => void;

/** the wheel unseats and rolls away down the aisle. Nobody chases it. */
function fxWheelRoll(ctx: FxContext, from: Vector3): Timeline {
  return new Timeline().then(BEAT * 3, ease.linear, (u) => {
    // House furniture leaving the building: constant velocity, no easing, no comment
    const x = from.x + u * 44;
    const y = Math.max(1.4, from.y - u * u * 7);
    for (let i = 0; i < 10; i++) {
      const a = (i / 10) * Math.PI * 2 + u * 26;
      ctx.sparks.push(x + Math.cos(a) * 4.4, y + Math.sin(a) * 4.4, from.z + Math.sin(a) * 1.2,
        0.85, mixHex(NEON.gruen.core, CREME.lit, u));
    }
  });
}

function shards(ctx: FxContext, at: Vector3, n: number, o: {
  spread?: number; up?: number; out?: number; gravity?: number; life?: number;
  size?: number; color: number; colorEnd: number; stretch?: number; axis?: 'radial' | 'lateral';
}): void {
  for (let i = 0; i < n; i++) {
    const a = Math.random() * Math.PI * 2;
    const r = Math.random() * (o.spread ?? 7);
    const side = Math.random() < 0.5 ? -1 : 1;
    const vx = o.axis === 'lateral' ? side * (9 + Math.random() * 22) : Math.cos(a) * (o.out ?? 12) * Math.random();
    ctx.sparks.spawn({
      pos: [at.x + Math.cos(a) * r, at.y + Math.sin(a) * r * (o.axis === 'lateral' ? 0.4 : 1), at.z],
      vel: [vx, (o.up ?? 8) * (0.4 + Math.random()), 3 + Math.random() * 10],
      gravity: o.gravity ?? 30, drag: 0.2, life: (o.life ?? 1.4) + Math.random(),
      size: o.size ?? 0.55, sizeEnd: 0.12,
      color: o.color, colorEnd: o.colorEnd, stretch: o.stretch ?? 0.05,
    });
  }
}

const BREAK: Record<BankBreakMode, BreakBeat> = {
  /** the vitrine goes: 90 cold shards, one gasp */
  glass(ctx, c, burst) {
    ctx.rig.kick(1.4);
    shards(ctx, burst, 90, { color: 0xbfe4ff, colorEnd: NEON.cyan.core, out: 16, up: 12 });
    ctx.coins.fountain(burst, new Vector3(0, 1, 0.5), 130, { power: 34, spread: 1.25, floorY: 0.35, life: 4.5 });
  },
  /** the drums leave their axles: three reels spin free and go sideways */
  axle(ctx, c, burst) {
    ctx.rig.kick(1.6);
    const api = c.api as { spinReel?(i: number): void };
    for (let i = 0; i < 3; i++) api.spinReel?.(i);
    shards(ctx, burst, 54, {
      color: MATERIALS.silber.lit, colorEnd: BAKELIT.shade, axis: 'lateral',
      up: 5, gravity: 34, size: 0.8, stretch: 0.09, spread: 4,
    });
    ctx.coins.fountain(burst, new Vector3(0.85, 0.5, 0.45), 110, { power: 30, spread: 1.5, floorY: 0.35, life: 4.2 });
  },
  /** the wheel unseats, hops the bowl and rolls out of frame */
  wheel(ctx, c, burst) {
    ctx.rig.kick(1.2);
    const api = c.api as { launch?(p: number): void };
    api.launch?.(2.6);
    shards(ctx, burst, 36, { color: NEON.gruen.halo, colorEnd: MATERIALS.gold.base, out: 9, up: 14, spread: 4.5 });
    ctx.anim.play(fxWheelRoll(ctx, burst.clone()));
    ctx.coins.fountain(burst, new Vector3(0, 1, 0.2), 110, { power: 26, spread: 1.4, floorY: 0.35, life: 4.5 });
  },
  /** the tray tears off its bolts: everything goes low, forward and loud */
  tray(ctx, c, burst) {
    ctx.rig.kick(1.5);
    const at = worldAnchor(c, 'tray');
    shards(ctx, at, 48, { color: MATERIALS.silber.base, colorEnd: BAKELIT.shade, out: 10, up: 4, gravity: 40, spread: 3 });
    ctx.coins.fountain(at, new Vector3(0, 0.55, 1), 130, { power: 27, spread: 1.5, floorY: 0.35, life: 4.0 });
  },
  /** the prize chute bursts: stuffing and tickets, and they take their time coming down */
  chute(ctx, c, burst) {
    ctx.rig.kick(1.1);
    const at = worldAnchor(c, 'tray');
    shards(ctx, at, 40, { color: PAPIER, colorEnd: SENF.base, out: 7, up: 15, gravity: 9, life: 2.4, size: 0.8, stretch: 0 });
    shards(ctx, burst, 24, { color: GUMMI.lit, colorEnd: BLUTROT.base, out: 6, up: 11, gravity: 12, life: 2.0, size: 0.9, stretch: 0 });
    ctx.coins.fountain(burst, new Vector3(0, 1, 0.7), 90, { power: 22, spread: 1.6, floorY: 0.35, life: 5.0 });
  },
};

/**
 * BANK SPRENGEN — the money shot.
 * Beat 1: the machine strains and the world slows to a quarter.
 * Beat 2: the thing that holds the money fails — glass, axle, wheel, tray or chute.
 * Beat 3: the coins land, all at once, and time comes back.
 */
export function fxBankBreak(ctx: FxContext, machine: MachineId, payout: number, mode: BankBreakMode = 'glass'): Timeline {
  const c = cab(ctx, machine);
  const tl = new Timeline();
  if (!c) return tl;
  const burst = worldAnchor(c, 'burst');
  const face = worldAnchor(c, 'face');

  tl.call(() => {
    ctx.say?.('BANK GESPRENGT', `${payout.toLocaleString('de-DE')} Münzen`);
    ctx.rig.toSpectacle(face);
    ctx.stage.timescale = SLOWMO_SCALE;
  });

  // beat 1 — the strain: the cabinet swells, the marquee overloads, everything holds
  tl.then(SLOWMO * 0.55, ease.inQuad, (u) => {
    c.group.scale.setScalar(1 + 0.035 * u);
    c.marquee.setIntensity(1 + u * 2.4);
    if (Math.random() < 0.35) {
      ctx.sparks.spawn({
        pos: [burst.x + (Math.random() - 0.5) * 8, burst.y + (Math.random() - 0.5) * 8, burst.z],
        vel: [0, 2, 2], life: 0.4, size: 0.5, sizeEnd: 0.05,
        color: NEON.cyan.core, colorEnd: NEON.cyan.halo,
      });
    }
  });

  // beat 2 — the glass. 240 shards, one gasp.
  tl.call(() => {
    ctx.rig.kick(1.4);
    for (let i = 0; i < 90; i++) {
      const a = Math.random() * Math.PI * 2;
      const r = Math.random() * 7;
      ctx.sparks.spawn({
        pos: [burst.x + Math.cos(a) * r, burst.y + Math.sin(a) * r, burst.z],
        vel: [Math.cos(a) * (6 + Math.random() * 16), Math.sin(a) * (6 + Math.random() * 14) + 6, 4 + Math.random() * 12],
        gravity: 30, drag: 0.2, life: 1.4 + Math.random(), size: 0.55, sizeEnd: 0.12,
        color: 0xbfe4ff, colorEnd: 0x2ee8ff, stretch: 0.05,
      });
    }
    ctx.coins.fountain(burst, new Vector3(0, 1, 0.5), 130, { power: 34, spread: 1.25, floorY: 0.35, life: 4.5 });
    c.group.scale.setScalar(1);
  });

  // the flash: one frame of white-hot cyan, then nothing
  tl.then(SNAP * 1.6, ease.pulse, (u) => {
    ctx.sparks.push(burst.x, burst.y, burst.z + 2, 26 * u, mixHex(NEON.cyan.halo, 0xffffff, u * 0.7));
  });

  // beat 3 — the deadpan hold, then time returns
  tl.wait(BEAT * 1.1)
    .call(() => { ctx.stage.timescale = 1; })
    .then(SETTLE * 2.4, ease.outCubic, (u) => c.marquee.setIntensity(lerp(3.4, 0.35, u)))
    .wait(BEAT)
    .call(() => { ctx.rig.toCabinet(face); c.marquee.setIntensity(0.35); });
  return tl;
}

/** BUST — the run of luck ends; the machine simply stops caring */
export function fxBust(ctx: FxContext, machine: MachineId): Timeline {
  const c = cab(ctx, machine);
  const tl = new Timeline();
  if (!c) return tl;
  tl.call(() => ctx.say?.('NICHTS', 'Der Automat behält den Einsatz'))
    .then(SNAP, ease.outQuad, (u) => c.marquee.setIntensity(1 - u * 0.75))
    .wait(BEAT)
    .then(SETTLE * 2, ease.outCubic, (u) => c.marquee.setIntensity(0.25 + u * 0.75));
  return tl;
}

/** REEL STOP — anticipation, overshoot, clunk. Slots live or die on this 200 ms. */
export function fxReelStop(ctx: FxContext, reel: number, symbol: number): Timeline {
  const c = cab(ctx, 'bandit');
  const tl = new Timeline();
  if (!c) return tl;
  const api = c.api as { stopReel(i: number, s: number): void };
  tl.call(() => api.stopReel(reel, symbol))
    .then(SNAP, ease.detent, () => { })
    .call(() => {
      const at = worldAnchor(c, 'face');
      for (let i = 0; i < 4; i++) {
        ctx.sparks.spawn({
          pos: [at.x + (reel - 1) * 3.3, at.y - 2, at.z],
          vel: [(Math.random() - 0.5) * 3, 1.5 + Math.random() * 2, 1],
          gravity: 12, life: 0.35, size: 0.4, sizeEnd: 0.02, color: NEON.magenta.core,
        });
      }
    });
  return tl;
}

/** ROULETTE settle — rattle, lip, drop. */
export function fxWheelSettle(ctx: FxContext, pocket: number): Timeline {
  const c = cab(ctx, 'roulette');
  const tl = new Timeline();
  if (!c) return tl;
  const api = c.api as { settle(p: number): void };
  tl.wait(BEAT * 0.6)
    .call(() => api.settle(pocket))
    .then(SETTLE, ease.detent, () => { })
    .call(() => {
      const at = worldAnchor(c, 'payout');
      for (let i = 0; i < 8; i++) {
        ctx.sparks.spawn({
          pos: [at.x, at.y, at.z], vel: [(Math.random() - 0.5) * 6, 3 + Math.random() * 3, (Math.random() - 0.5) * 6],
          gravity: 20, life: 0.5, size: 0.4, sizeEnd: 0.02, color: NEON.gruen.core,
        });
      }
    });
  return tl;
}

/** CLAW — the tension is the drop, not the grab */
export function fxClaw(ctx: FxContext, x: number, z: number, success: boolean): Timeline {
  const c = cab(ctx, 'greifer');
  const tl = new Timeline();
  if (!c) return tl;
  const api = c.api as { moveClaw(x: number, z: number): void; dropClaw(v: number): void; gripClaw(v: number): void };
  tl.then(SETTLE * 1.4, ease.inOutCubic, (u) => api.moveClaw(x * u, z * u))
    .wait(BEAT * 0.7)
    .then(SETTLE, ease.inQuad, (u) => api.dropClaw(u))
    .then(SNAP * 2, ease.outBack, (u) => api.gripClaw(u))
    .wait(BEAT * 0.5)
    .then(SETTLE * 1.4, ease.outCubic, (u) => api.dropClaw(1 - u))
    .call(() => { if (!success) ctx.say?.('MÜDER GREIFER', 'Der Griff hält nicht'); })
    .then(SNAP * 2, ease.outQuad, (u) => api.gripClaw(success ? 1 : 1 - u))
    .then(SETTLE * 1.6, ease.inOutCubic, (u) => api.moveClaw(lerp(x, -5.5, u), lerp(z, 4.5, u)));
  return tl;
}

/** the sign gag: SPIELHALLE → the "a" dies → SPIELHÖLLE. Canon, and a real light source. */
export function fxSignGag(ctx: FxContext, to: 0 | 1 = 1): Timeline {
  const tl = new Timeline();
  const s = ctx.room.sign;
  if (to === 1) {
    tl.call(() => s.setGag(0))
      .then(0.07, ease.linear, () => s.setGag(0.7))
      .then(0.07, ease.linear, () => s.setGag(0))
      .then(0.14, ease.linear, () => s.setGag(0.9))
      .then(0.07, ease.linear, () => s.setGag(0.1))
      .wait(0.05)
      .then(0.21, ease.linear, () => s.setGag(1))
      .call(() => ctx.say?.('SPIELHÖLLE', 'Die Tür fällt zu'))
      .wait(BEAT * 3)
      .call(() => s.setGag(-1));
  } else {
    tl.then(0.28, ease.linear, () => s.setGag(0.2))
      .then(0.14, ease.linear, () => s.setGag(0));
  }
  return tl;
}

/** DIE ABRECHNUNG — Der Croupier presents the ledger and the room holds still */
export function fxAbrechnung(ctx: FxContext, due: number, paid: number, confiscated: string[]): Timeline {
  const tl = new Timeline();
  const short = due - paid;
  tl.call(() => {
    ctx.say?.('DIE ABRECHNUNG', `Fällig ${due.toLocaleString('de-DE')} · gezahlt ${paid.toLocaleString('de-DE')}`);
    ctx.croupier.group.visible = true;
    ctx.rig.peek(1);
  })
    .then(SETTLE * 2, ease.inOutCubic, (u) => ctx.croupier.setPose(u))
    .wait(BEAT * 2);
  if (short > 0) {
    tl.call(() => ctx.say?.('ER NIMMT ETWAS MIT', confiscated.join(' · ') || 'Ihre beste Marke'))
      .then(SNAP * 2, ease.outQuad, () => ctx.setVignette?.(0.7))
      .wait(BEAT * 2)
      .then(SETTLE * 2, ease.outCubic, (u) => ctx.setVignette?.(0.7 * (1 - u)));
  }
  tl.then(SETTLE * 2, ease.inOutCubic, (u) => ctx.croupier.setPose(1 - u))
    .call(() => ctx.rig.unpeek());
  return tl;
}

/**
 * THE RECEIPT — death (or victory) prints from a till roll. The anecdote-export button.
 * The paper unrolls, the printer clatters, then it is torn off and falls out of frame.
 */
export function fxReceipt(ctx: FxContext, lines: string[]): Timeline {
  const tl = new Timeline();
  const tex = paperTexture(lines, 340, 26);
  const aspect = (tex.image as HTMLCanvasElement).height / (tex.image as HTMLCanvasElement).width;
  const W = 8.5;
  const H = W * aspect;

  const strip = new Mesh(new PlaneGeometry(W, H), toon({
    color: 0xffffff, ramp: 'SOFT2', rim: 0.2, halftone: 0, side: DoubleSide,
    map: tex, transparent: true,
  }));
  strip.userData.noInk = true;
  const pivot = new Group();
  pivot.add(strip);
  ctx.fxRoot.add(pivot);

  const c = cab(ctx);
  const at = c ? worldAnchor(c, 'face') : new Vector3(0, 16, 0);
  at.z += 9;
  at.x -= 1.5;
  pivot.position.copy(at);
  pivot.position.y += H * 0.5;

  const steps = 16;
  tl.call(() => ctx.say?.('KASSENBON', 'Der Automat druckt Ihren Abend aus'));
  for (let i = 0; i < steps; i++) {
    tl.then(RATCHET * 1.6, ease.detent, (u) => {
      const k = (i + u) / steps;
      strip.scale.y = k;
      strip.position.y = -H * k * 0.5;
      pivot.rotation.z = Math.sin((i + u) * 0.9) * 0.012;
    });
  }
  tl.wait(BEAT)
    .then(SNAP, ease.outQuad, (u) => { pivot.rotation.z = 0.05 * u; })
    .wait(BEAT * 2.4)
    .then(SETTLE * 3, ease.inQuad, (u) => {
      pivot.position.y = at.y + H * 0.5 - u * 30;
      pivot.rotation.z = 0.05 + u * 0.8;
      (strip.material as { opacity: number }).opacity = 1 - u * u;
    })
    .call(() => { pivot.removeFromParent(); tex.dispose(); });
  return tl;
}

/** the floor transition: Der Croupier wheels the cabinets down one level */
export function fxFloorStart(ctx: FxContext, floor: number): Timeline {
  const tl = new Timeline();
  tl.call(() => {
    ctx.say?.(`${floor}. ETAGE`, floor > 1
      ? 'Der Croupier schiebt die Automaten hinunter'
      : 'Die Nacht beginnt');
    ctx.setVignette?.(1);
  })
    .then(SETTLE * 2, ease.outCubic, (u) => ctx.setVignette?.(1 - u));
  // Floor 1 is the front door, not a move-in: nothing was wheeled, so nobody crosses.
  if (floor > 1) {
    const g = ctx.croupier.group;
    tl.then(BEAT * 4, ease.inOutCubic, (u) => {
      ctx.rig.driftAlong(-30, 24, u);
      // the service lane BEHIND the cabinet row (cabinets at z≈-14, back wall at -26):
      // he rolls his dolly along it, glimpsed in the gaps, never through a machine
      g.position.set(lerp(-52, 56, u), 0, -23.5);
      g.rotation.y = Math.PI * 0.5;
    })
      // and back to his mark by the last cabinet, exactly as kit.ts placed him
      .call(() => {
        g.position.set(62, 0, -20);
        g.rotation.set(0, -0.85, 0);
      });
  }
  return tl;
}

/** small in-world beats that need no camera */
export function fxSplit(ctx: FxContext, at: Vector3): Timeline {
  return new Timeline().call(() => {
    for (let i = 0; i < 14; i++) {
      const a = Math.random() * Math.PI * 2;
      ctx.sparks.spawn({
        pos: [at.x, at.y, at.z], vel: [Math.cos(a) * 7, 4 + Math.random() * 4, Math.sin(a) * 7],
        gravity: 24, life: 0.6, size: 0.45, sizeEnd: 0.05, color: NEON.cyan.core, colorEnd: 0xffffff,
      });
    }
  });
}

export function fxZins(ctx: FxContext, at: Vector3, value: number): Timeline {
  return new Timeline().then(BEAT, ease.outCubic, (u) => {
    ctx.sparks.push(at.x, at.y + 1.4 + u * 2.4, at.z, 1.1 * (1 - u), NEON.amber.core);
  }).call(() => void value);
}

/**
 * SCORE — the pocket pays. `tags` (v1) is the token's enchantment list *at the moment it
 * scored*, carried on the event because the token is usually consumed the same tick and
 * nothing can look it up afterwards. So the burst is tinted by the combo's loudest tag:
 * a [Fluch] score reads violet, a [Gluecks] score green, a plain one amber. Colour alone
 * is never a distinction in this game (§10), but here it is a *second* channel on top of a
 * burst that already happened — it names which coin just paid.
 */
export function fxScore(ctx: FxContext, at: Vector3, value: number, tags: Tag[] = []): Timeline {
  const tag = loudestTag(tags);
  const core = tag ? TAG_COLOR[tag] : NEON.amber.core;
  const halo = tag ? mixHex(core, 0xffffff, 0.45) : NEON.amber.halo;
  return new Timeline().call(() => {
    const n = Math.max(3, Math.min(16, Math.round(value / 8)));
    for (let i = 0; i < n; i++) {
      ctx.sparks.spawn({
        pos: [at.x, at.y, at.z],
        vel: [(Math.random() - 0.5) * 6, 3 + Math.random() * 5, (Math.random() - 0.5) * 4],
        gravity: 18, life: 0.5 + Math.random() * 0.3, size: 0.45, sizeEnd: 0.04,
        color: core, colorEnd: halo,
      });
    }
  });
}

/**
 * [Echo] — the after-image detaches and replays (ART-BIBLE §4.1). v1 carries `at`, so the
 * ghost is born where the coin actually was instead of a frame late at the origin.
 */
export function fxEcho(ctx: FxContext, at: Vector3, value: number): Timeline {
  const drift = new Vector3((Math.random() - 0.5) * 2.4, 1.1, -1.6);
  return new Timeline().then(SETTLE * 1.4, ease.outCubic, (u) => {
    const s = 1.9 * (1 - u * 0.55);
    ctx.sparks.push(at.x + drift.x * u, at.y + drift.y * u, at.z + drift.z * u, s,
      mixHex(TAG_COLOR.Echo, NACHT.void, 0.35 + u * 0.6));
  }).call(() => void value);
}

/** [Kleber] — the token welds into the field. Tar, not sparkle: it stops, it stays. */
export function fxStick(ctx: FxContext, at: Vector3): Timeline {
  return new Timeline()
    .call(() => {
      for (let i = 0; i < 5; i++) {
        const a = Math.random() * Math.PI * 2;
        ctx.smoke.spawn({
          pos: [at.x, at.y, at.z],
          vel: [Math.cos(a) * 1.6, 0.4 + Math.random(), Math.sin(a) * 1.6],
          gravity: 9, drag: 2.2, life: 0.7 + Math.random() * 0.4,
          size: 0.5, sizeEnd: 0.18, color: SENF.base, colorEnd: SENF.shade, fade: 'hold',
        });
      }
    })
    // the hanging drop stretches under gravity and does not fall
    .then(SETTLE, ease.outBack, (u) => {
      ctx.smoke.push(at.x, at.y - 0.35 - u * 0.5, at.z, 0.85 * (1 - u * 0.4), SENF.base);
    });
}

/**
 * A BEAT — the minimal honest acknowledgement of an event: one caption, one light change,
 * one puff. Wave-2 owns the run shell's real set pieces (Saalchef doors, the Rausschmeißer
 * choreography); until then every v1 shell event still *lands*, in the room, in the game's
 * own vocabulary, instead of being silently dropped.
 */
export function fxBeat(ctx: FxContext, o: {
  text: string; sub?: string; machine?: MachineId | null; color?: number;
  n?: number; lift?: number; vignette?: number;
}): Timeline {
  const tl = new Timeline();
  const c = cab(ctx, o.machine);
  const color = o.color ?? (c ? NEON[MACHINE_LOOK[c.id].accent].core : NEON.cyan.core);
  const at = c ? worldAnchor(c, 'face') : new Vector3(0, 15, -6);
  const n = o.n ?? 10;

  tl.call(() => { ctx.say?.(o.text, o.sub); if (o.vignette) ctx.setVignette?.(0); });
  if (o.vignette) tl.then(SNAP * 2, ease.outQuad, (u) => ctx.setVignette?.(u * o.vignette!));
  tl.then(ANTIC, ease.inQuad, (u) => c?.marquee.setIntensity(1 - 0.45 * u))
    .call(() => {
      for (let i = 0; i < n; i++) {
        const a = (i / n) * Math.PI * 2;
        ctx.sparks.spawn({
          pos: [at.x + Math.cos(a) * 1.6, at.y + (o.lift ?? 1.5), at.z + 0.6],
          vel: [Math.cos(a) * 4.5, 3 + Math.random() * 3.5, Math.sin(a) * 3],
          gravity: 16, life: 0.6 + Math.random() * 0.4, size: 0.55, sizeEnd: 0.04,
          color, colorEnd: mixHex(color, NACHT.void, 0.7),
        });
      }
    })
    .then(SNAP, ease.outQuad, (u) => c?.marquee.setIntensity(0.55 + 1.05 * u))
    .then(SETTLE, ease.outBack, (u) => c?.marquee.setIntensity(lerp(1.6, 1, u)));
  if (o.vignette) {
    tl.wait(BEAT * 0.5)
      .then(SETTLE * 1.6, ease.inOutCubic, (u) => ctx.setVignette?.(o.vignette! * (1 - u)));
  }
  return tl;
}

// ================================================================ dispatch

/** sim ticks → seconds. The sim runs at 120 Hz; the contract only ever speaks in ticks. */
const TICK_HZ = 120;
export const ticks = (n: number): number => n / TICK_HZ;

/**
 * The event → animation table. This is the contract's `SimEvent` union, exhaustively.
 * A wave-2 agent adding an event adds a line here and nothing else.
 *
 * Every `at` on an event is a WORLD position in the sim's shared Rapier world (contract
 * v1), so it goes through `fxWorld` — never straight into a Vector3.
 */
export function dispatch(ctx: FxContext, ev: SimEvent): void {
  switch (ev.t) {
    case 'payout':
      ctx.anim.play(fxPayout(ctx, ev.muenzen, ev.tickets, ev.machine));
      break;
    case 'payoutStage':
      ctx.anim.play(fxPayoutStage(ctx, ev.stage, ev.before, ev.after, ev.machine));
      break;
    case 'topfFeed': {
      const c = cab(ctx, ev.machine);
      ctx.anim.play(fxTopfFeed(ctx, ev.machine, ev.amount));
      if (c) {
        const before = ctx.hud.topf01;
        ctx.hud.topf01 = Math.min(1, before + ev.amount / Math.max(1, ctx.hud.topfMax));
        ctx.anim.play(fxTopfLevel(c, before, ctx.hud.topf01));
      }
      break;
    }
    case 'jackpot': ctx.anim.playBig(() => fxJackpot(ctx, ev.machine, ev.value)); break;
    case 'bankBreak': ctx.anim.playBig(() => fxBankBreak(ctx, ev.machine, ev.payout, ev.mode)); break;
    case 'bust': ctx.anim.play(fxBust(ctx, ev.machine)); break;
    case 'tilt': ctx.anim.play(fxTilt(ctx, ev.machine, ev.amount)); break;
    case 'tiltLockout':
      ctx.lockouts.set(ev.machine, ev.until);
      ctx.anim.play(fxTiltLockout(ctx, ev.machine, Math.max(SNAP, ticks(ev.until - ctx.hud.tick))));
      break;
    case 'straehne': ctx.anim.play(fxStraehne(ctx, ev.from, ev.to, ev.machine)); break;
    case 'glueckSpent': ctx.anim.play(fxGlueck(ctx, ev.stream)); break;
    case 'glueckRegen':
      ctx.anim.play(new Timeline().then(BEAT, ease.pulse, (u) => {
        const c = cab(ctx);
        if (!c) return;
        const at = worldAnchor(c, 'face');
        ctx.sparks.push(at.x, at.y + 4 + u * 2, at.z, 1.2 * u, NEON.gruen.core);
      }));
      break;
    case 'verdacht': ctx.anim.play(fxVerdacht(ctx, ev.delta, ev.reason)); break;
    case 'reelStop': ctx.anim.play(fxReelStop(ctx, ev.reel, ev.symbolToken)); break;
    case 'wheelSettle': ctx.anim.play(fxWheelSettle(ctx, ev.pocket)); break;
    case 'abrechnung': ctx.anim.playBig(() => fxAbrechnung(ctx, ev.due, ev.paid, ev.confiscated)); break;
    case 'receipt': ctx.anim.playBig(() => fxReceipt(ctx, ev.lines)); break;
    case 'floorStart': ctx.anim.playBig(() => fxFloorStart(ctx, ev.floor)); break;
    case 'split': ctx.anim.play(fxSplit(ctx, fxWorld(ctx, ev.at))); break;
    case 'echo': ctx.anim.play(fxEcho(ctx, fxWorld(ctx, ev.at), ev.value)); break;
    case 'stick': ctx.anim.play(fxStick(ctx, fxWorld(ctx, ev.at))); break;
    case 'score': ctx.anim.play(fxScore(ctx, fxWorld(ctx, ev.at), ev.value, ev.tags)); break;
    case 'zinsTick': {
      const c = cab(ctx);
      ctx.anim.play(fxZins(ctx, c ? worldAnchor(c, 'face') : new Vector3(0, 12, 0), ev.newValue));
      break;
    }
    case 'collision':
      ctx.tokens.impact(ev.a, ev.impulse, fxLocal(ctx, ev.at), ev.n ? new Vector3(...ev.n) : undefined);
      break;
    /** **v1** — buried coins became fixed bodies; stop paying for their transforms. */
    case 'sediment': ctx.tokens.setPacked(ev.token, ev.packed); break;

    // ---- the run shell (v1). Minimal, native acknowledgements: another agent is
    // building the real set pieces, and a half-built one would fight it. -------------
    case 'floorEnd':
      ctx.anim.play(fxBeat(ctx, {
        text: `${ev.floor}. ETAGE ABGERÄUMT`, sub: 'Der Croupier holt die Automaten',
        color: NEON.amber.core, vignette: 0.55, n: 8,
      }));
      break;
    case 'quotaMet':
      ctx.anim.play(fxBeat(ctx, {
        text: 'SOLL ERFÜLLT', sub: `${ev.tickets.toLocaleString('de-DE')} Tickets · die Tür geht auf`,
        color: NEON.gruen.core, n: 14,
      }));
      break;
    case 'bossStart':
      ctx.anim.playBig(() => fxBeat(ctx, {
        text: 'DER SAALCHEF', sub: ev.boss, color: NEON.violet.core, vignette: 0.8, n: 12,
      }));
      break;
    case 'bossPhase':
      ctx.anim.play(fxBeat(ctx, {
        text: `REGEL ${ev.phase}`, sub: ev.rule, color: NEON.violet.core, n: 8,
      }));
      break;
    case 'bossEnd':
      ctx.anim.playBig(() => fxBeat(ctx, {
        text: ev.won ? 'DER SAALCHEF GIBT NACH' : 'DER SAALCHEF BLEIBT',
        sub: ev.boss, color: ev.won ? NEON.gruen.core : BLUTROT.lit,
        vignette: ev.won ? 0 : 0.7, n: ev.won ? 16 : 6,
      }));
      break;
    case 'rausschmeisser':
      if (ev.phase === 'shove') ctx.anim.play(fxTilt(ctx, ev.machine, 1.4));
      ctx.anim.play(fxBeat(ctx, {
        machine: ev.machine, color: ev.phase === 'win' ? NEON.gruen.core : BLUTROT.lit,
        text: 'DER RAUSSCHMEISSER',
        sub: { arrive: 'Er lehnt sich an den Automaten', shove: 'Er rüttelt', win: 'Er lässt ab', lose: 'Er nimmt den Automaten' }[ev.phase],
        vignette: ev.phase === 'arrive' ? 0.6 : 0, n: 6,
      }));
      break;
    case 'hausverbot':
      ctx.anim.play(ev.active
        ? fxBeat(ctx, { text: 'HAUSVERBOT', sub: 'Die Automaten nehmen nur noch Tickets', color: BLUTROT.lit, vignette: 0.85, n: 5 })
        : fxBeat(ctx, { text: 'HAUSVERBOT AUFGEHOBEN', sub: 'Man kennt Sie hier trotzdem', color: NEON.gruen.core, n: 8 }));
      break;
    case 'grab':
      ctx.anim.play(fxBeat(ctx, {
        machine: 'greifer', color: ev.kept ? NEON.gruen.core : SENF.base,
        text: ev.kept ? 'GEGRIFFEN' : 'DER GRIFF HÄLT NICHT',
        sub: ev.prize ?? 'Nichts im Schacht', n: ev.kept ? 12 : 5,
      }));
      break;
    case 'item':
      ctx.anim.play(fxBeat(ctx, {
        text: ev.gained ? 'ERWORBEN' : 'ABGEGEBEN', sub: ev.name,
        color: ev.gained ? NEON.gruen.core : BLUTROT.lit, n: ev.gained ? 12 : 5,
      }));
      break;
    case 'zinkenStolen':
      ctx.anim.play(fxBeat(ctx, {
        machine: ev.machine, text: 'ZINKEN ABGERISSEN', sub: ev.zinken,
        color: NEON.violet.core, n: 9,
      }));
      break;

    case 'phase':
    case 'drop':
    case 'cashOut':
    case 'nachlegen':
    case 'zinkenInstalled':
    case 'zinkenDiscovered':
      break;
    default:
      // an unknown event is a contract we have not caught up with yet, never a crash
      break;
  }
}

export { Timeline };
